import React, { Component } from "react";
import { Form, Row, Col, Button, Alert, Modal, InputGroup } from "react-bootstrap";
import { get, post, put } from "aws-amplify/api";
import { Link } from "react-router-dom";
import { LinkContainer } from "react-router-bootstrap";
import { GenericTable } from "components/GenericTable";
import "containers/common/List.css";
import DatePicker from "react-datepicker";
import Select from "react-select";
import Moment from "moment";
import HeadOfficeModal from "containers/headoffice/HeadOfficeModal";
import { createColumnHelper } from "@tanstack/react-table";

import { PacmanLoader } from "react-spinners";
import { isDatePastOrPresent, parseDate } from "../../utils/DateUtil";

function buildLegacyCellInfo(info, legacyColumn) {
  return {
    value: info.getValue(),
    original: info.row.original,
    index: info.row.index,
    column: { id: legacyColumn.id },
  };
}

function ReactTable({ data, columns, defaultPageSize = 25, sortable = true }) {
  const columnHelper = React.useMemo(() => createColumnHelper(), []);

  const adaptedColumns = React.useMemo(
    () =>
      columns.map((legacyColumn) => {
        const cellRenderer = (info) => {
          if (typeof legacyColumn.Cell === "function") {
            return legacyColumn.Cell(buildLegacyCellInfo(info, legacyColumn));
          }

          return info.getValue();
        };

        const columnConfig = {
          id: legacyColumn.id,
          header: legacyColumn.Header,
          cell: cellRenderer,
          size: legacyColumn.maxWidth,
          enableSorting: sortable,
          meta: {
            align: legacyColumn.style?.textAlign,
          },
        };

        if (typeof legacyColumn.accessor === "function") {
          return columnHelper.accessor((row) => legacyColumn.accessor(row), columnConfig);
        }

        return columnHelper.accessor(legacyColumn.accessor || legacyColumn.id, columnConfig);
      }),
    [columnHelper, columns, sortable]
  );

  return (
    <GenericTable
      columns={adaptedColumns}
      data={data || []}
      pageSize={defaultPageSize}
      enablePagination={true}
      hasHeaderSorting={sortable}
    />
  );
}

export default class LeaseList extends Component {
  constructor(props) {
    super(props);

    this.updateTableState = this.updateTableState.bind(this);
    this.saveHeadOfficeRecord = this.saveHeadOfficeRecord.bind(this);
    this.toggleHeadOfficeSearchDialog = this.toggleHeadOfficeSearchDialog.bind(this);
    this.isCellEditing = false;
    this.saveChain = Promise.resolve();
    this.editSequence = 0;

    this.state = {
      searching: false,
      isLoading: true,
      filter: {},
      data: [],
      dataSet: "leases",
      dataRecord: "leases",
      dataSetPrimaryKey: "lease_id",
      showAlert: false,
      errorMessage: "",
      savingNewRecord: false,
      buildingNameOptions: [],
      tenantBuildingNameOptions: [],
      selectedPropertyBuildingName: { value: null, label: null },
      selectedTenantBuildingName: { value: null, label: null },
      stillAnOccupantFilter: true,
      archiveFilter: false,
      sortOptions: [
        { value: "Level No.", label: "Level No." },
        { value: "Lessee Trading", label: "Lessee Trading" },
      ],
      selectedSortOption: { value: "Level No.", label: "Level No." },
      registered_lease: null,
      tenant: null,
      showMergeDialog: false,
      headofficeleases: [], // Not used in this context, but HeadOfficeList requires it
      headOfficeMatches: [],
      showHeadOfficeSearchDialog: false,
      lastLeaseIdSelected: null,
    };

    this.latestEditedData = null;
  }

  async componentDidMount() {
    if (!this.props.isAuthenticated) {
      // if false
      return;
    }
    this.applyFilters();
    const buildingNameOptions = this.buildPropertyBuildingNameOptions(this.props.buildingNameOptions);
    const tenantBuildingNameOptions = this.buildTenantBuildingNameOptions(this.props.data);

    this.setState({
      buildingNameOptions,
      tenantBuildingNameOptions,
      selectedPropertyBuildingName: { value: null, label: null },
      selectedTenantBuildingName: { value: null, label: null },
    });

    this.setState({ isLoading: false });
  }

  componentDidUpdate(prevProps) {
    if (this.props.buildingNameOptions !== prevProps.buildingNameOptions) {
      const buildingNameOptions = this.buildPropertyBuildingNameOptions(this.props.buildingNameOptions);
      this.setState({ buildingNameOptions });

      const selectedPropertyBuildingName = this.state.selectedPropertyBuildingName;
      if (selectedPropertyBuildingName?.value && !buildingNameOptions.some((o) => o.value === selectedPropertyBuildingName.value)) {
        this.setState({ selectedPropertyBuildingName: { value: null, label: null } });
      }
    }

    if (this.props.data !== prevProps.data) {
      if (!this.isCellEditing) {
        this.applyFilters();

        const tenantBuildingNameOptions = this.buildTenantBuildingNameOptions(this.props.data);
        this.setState({ tenantBuildingNameOptions });

        const selectedTenantBuildingName = this.state.selectedTenantBuildingName;
        if (selectedTenantBuildingName?.value && !tenantBuildingNameOptions.some((o) => o.value === selectedTenantBuildingName.value)) {
          this.setState({ selectedTenantBuildingName: { value: null, label: null } });
        }
      }
    }
  }

  buildPropertyBuildingNameOptions = (buildingNameRows = []) => {
    const options = [];
    const seen = new Set();

    for (let i = 0; i < buildingNameRows.length; i++) {
      const row = buildingNameRows[i];
      const name = typeof row === "string" ? row : row?.building_name;
      if (!name || seen.has(name)) {
        continue;
      }

      seen.add(name);
      options.push({ label: name, value: name });
    }

    return options;
  };

  buildTenantBuildingNameOptions = (leaseRows = []) => {
    const options = [];
    const seen = new Set();

    for (let i = 0; i < leaseRows.length; i++) {
      const name = leaseRows[i]?.building_name;
      if (!name || seen.has(name)) {
        continue;
      }

      seen.add(name);
      options.push({ label: name, value: name });
    }

    return options;
  };

  handleCellFocus = () => {
    this.isCellEditing = true;
  };

  queueSave = (cellInfo, event) => {
    this.saveChain = this.saveChain.then(() => this.handleSave(cellInfo, event));
    return this.saveChain;
  };

  handleCellBlur = async (cellInfo, event) => {
    await this.queueSave(cellInfo, event);
    this.isCellEditing = false;
    this.applyFilters();
  };

  handleLesseeTradingBlur = async (cellInfo, event) => {
    await this.handleLesseeTradingUpdated(cellInfo, event);
    this.isCellEditing = false;
    this.applyFilters();
  };

  async handleDataSave(data) {
    try {
      const saveLeaseRecord = this.props.saveLeaseRecord || this.props.saveRecord;
      return await saveLeaseRecord(data, this.state.dataSet, this.state.dataSetPrimaryKey);
    } catch (e) {
      alert("Error saving");
    }
  }

  handleDeleteRecord = async (cellInfo, event) => {
    event.preventDefault();
    let data = [...this.props.data];
    var r = window.confirm("Are you sure you want to delete this record?");
    if (r === true) {
      data[cellInfo.index].archived = true;
      data[cellInfo.index].archived_date = new Date();
      await this.handleDataSave(data[cellInfo.index]).then((result) => {
        data.splice(cellInfo.index, 1);
        this.props.updateTableState(data, this.state.dataSet);
      });
    }
  };

  handleInputChange = (cellInfo, event) => {
    let data = Array.isArray(this.latestEditedData) ? [...this.latestEditedData] : [...this.props.data];
    var indexOfRecord = data.findIndex((i) => i.lease_id === cellInfo.original.lease_id);
    if (data[indexOfRecord].archived === false) {
      this.editSequence += 1;
      data[indexOfRecord][cellInfo.column.id] = event.target.value;

      data[indexOfRecord].modified = true;
      data[indexOfRecord]._localEditSeq = this.editSequence;
      this.latestEditedData = data;
      const editedRecord = { ...data[indexOfRecord] };
      this.setState((prevState) => ({
        data: (prevState.data || []).map((row) =>
          row.lease_id === editedRecord.lease_id ? { ...row, ...editedRecord } : row
        ),
      }));
    }
  };

  handleNumberChange = (cellInfo, event) => {
    let data = Array.isArray(this.latestEditedData) ? [...this.latestEditedData] : [...this.props.data];
    var indexOfRecord = data.findIndex((i) => i.lease_id === cellInfo.original.lease_id);
    if (data[indexOfRecord].archived === false) {
      this.editSequence += 1;
      let val = event.target.value.replace(/[^0-9.-]/g, "");
      if (event.target.value !== "") {
        data[indexOfRecord][cellInfo.column.id] = Number(val);
      } else {
        data[indexOfRecord][cellInfo.column.id] = "";
      }
      data[indexOfRecord].modified = true;
      data[indexOfRecord]._localEditSeq = this.editSequence;
      this.latestEditedData = data;
      const editedRecord = { ...data[indexOfRecord] };
      this.setState((prevState) => ({
        data: (prevState.data || []).map((row) =>
          row.lease_id === editedRecord.lease_id ? { ...row, ...editedRecord } : row
        ),
      }));
    }
  };

  handleCheckboxChange = (cellInfo, event) => {
    let data = Array.isArray(this.latestEditedData) ? [...this.latestEditedData] : [...this.props.data];
    var indexOfRecord = data.findIndex((i) => i.lease_id === cellInfo.original.lease_id);
    if (data[indexOfRecord].archived === false || cellInfo.column.id === "selected") {
      data[indexOfRecord][cellInfo.column.id] = event.target.checked;
      data[indexOfRecord].modified = true;
      if (data[indexOfRecord].lease_number == null || data[indexOfRecord].lease_number === "") {
        data[indexOfRecord].registered = false;
      } else {
        data[indexOfRecord].registered = true;
      }
      if (data[indexOfRecord].still_an_occ === false) {
        data[indexOfRecord].include_cs_text = false;
      }

      this.latestEditedData = data;
      this.applyFilters();
      this.props.updateTableState(data, this.state.dataSet);
    }
  };

  handleDateChange = (cellInfo, event) => {
    let data = Array.isArray(this.latestEditedData) ? [...this.latestEditedData] : [...this.props.data];
    var indexOfRecord = data.findIndex((i) => i.lease_id === cellInfo.original.lease_id);
    if (data[indexOfRecord].archived === false) {
      let dateVal = null;

      if (event) {
        if (cellInfo.column.id === "expiry_date") {
          const parsedMoment = Moment(event);
          dateVal = parsedMoment.isValid() ? parsedMoment.local().format("YYYY-MM-DD") : null;
        } else {
          const parsedDate = parseDate(event);
          dateVal = parsedDate || null;
        }
      }

      data[indexOfRecord][cellInfo.column.id] = dateVal;
      data[indexOfRecord].modified = true;
      this.latestEditedData = data;
      this.applyFilters();
      this.props.updateTableState(data, this.state.dataSet);
    }
  };

  handleDropdownChange = (cellInfo, event) => {
    let data = Array.isArray(this.latestEditedData) ? [...this.latestEditedData] : [...this.props.data];
    var indexOfRecord = data.findIndex((i) => i.lease_id === cellInfo.original.lease_id);
    if (event) {
      if (data[indexOfRecord].archived === false) {
        data[indexOfRecord][cellInfo.column.id] = event.value;
        data[indexOfRecord].modified = true;
        this.latestEditedData = data;
        this.applyFilters();
        this.props.updateTableState(data, this.state.dataSet);
        this.queueSave(cellInfo, event);
      }
    } else {
      if (data[indexOfRecord].archived === false) {
        data[indexOfRecord][cellInfo.column.id] = null;
        data[indexOfRecord].modified = true;
        this.latestEditedData = data;
        this.applyFilters();
        this.props.updateTableState(data, this.state.dataSet);
        this.queueSave(cellInfo, event);
      }
    }
  };

  /**
   * from App/src/containers/property/LeaseDetail.js
   */
  async saveHeadOfficeRecord(record, endpoint, id) {
    record.last_edited_by = this.props.user;

    //If the record is a new record perform a post
    if (record.hasOwnProperty("new_record") && record.new_record === true) {
      record.last_edited_by = this.props.user;

      if (endpoint === "headOfficeLeases") {
        const { body } = await post({
          apiName: "colin",
          path: `/headoffices/${record.led_head_office_id}/headofficeleases/new`,
          options: { body: record },
        }).response;

        return await body.json();
      }
    }

    //If not a new record and updating an existing, perform a put
    if (endpoint === "headOfficeLeases") {
      const { body } = await put({
        apiName: "colin",
        path: `/headoffices/${record.led_head_office_id}/headofficeleases/${record[id]}`,
        options: { body: record },
      }).response;

      return await body.json();
    }
  }

  async updateTableState(data, set) {
    let newData = this.state.data;

    for (const leaseWithHeadOffice of data) {
      let foundLeaseIdx = newData.findIndex((d) => d.lease_id === leaseWithHeadOffice.lease_id);
      if (foundLeaseIdx < 0) continue;
      newData[foundLeaseIdx].led_head_office_lease_id = leaseWithHeadOffice.lease_id;
    }

    this.setState({ data: newData });
  }

  async toggleHeadOfficeSearchDialog() {
    this.setState({ showHeadOfficeSearchDialog: !this.state.showHeadOfficeSearchDialog });
  }

  async getHeadOfficeMatches(filter) {
    try {
      const { body } = await get({
        apiName: "colin",
        path: `/headoffices`,
        options: {
          queryParams: filter,
        },
      }).response;

      return await body.json();
    } catch (error) {
      alert(error + ". Please refresh the page (F5) to try again.");
    }
  }

  handleLesseeTradingUpdated = async (thisArg, argArray) => {
    const editedRow = Array.isArray(this.latestEditedData)
      ? this.latestEditedData.find((row) => row.lease_id === thisArg.original.lease_id)
      : null;
    const lesseeTradingName = (editedRow?.lessee_trading ?? thisArg.original.lessee_trading) ?? "";
    const headOfficeMatches = await this.getHeadOfficeMatches({
      name: lesseeTradingName,
      state: thisArg.original.state ?? "",
    });

    if (headOfficeMatches && headOfficeMatches.length > 0) {
      this.setState({ headOfficeMatches });
      this.setState({ showHeadOfficeSearchDialog: true });
      this.setState({ lastLeaseIdSelected: thisArg.original.lease_id });
    }

    await this.queueSave(thisArg, argArray);
  };

  handleSave = async (cellInfo, event) => {
    this.setState({ showAlert: false });
    let data = Array.isArray(this.latestEditedData) ? [...this.latestEditedData] : [...this.props.data];

    // Iterate through each record - we need to find a way to speed this up
    for (var i = 0; i < data.length; i++) {
      const validation = this.props.validateRelatedRecord(data[i], this.state.dataSet);

      // Check if modified
      if (data[i].hasOwnProperty("modified")) {
        // Check if modified
        if (data[i].modified === true) {
          // Check if valid
          if (validation[0] === true) {
            //Save the data
            if (!this.state.savingNewRecord) {
              // Check if new record
              if (data[i].hasOwnProperty("new_record")) {
                if (data[i].new_record === true) {
                  this.setState({ savingNewRecord: true });
                }
              }
              const savedLeaseId = data[i].lease_id;
              const savedLocalSeq = data[i]._localEditSeq || 0;
              const result = await this.handleDataSave(data[i]);

              // Reset state to no longer modified
              if (result.result) {
                const latestData = Array.isArray(this.latestEditedData) ? [...this.latestEditedData] : [...data];
                const latestIdx = latestData.findIndex((row) => row.lease_id === savedLeaseId);

                if (latestIdx >= 0) {
                  const latestLocalSeq = latestData[latestIdx]._localEditSeq || 0;

                  // Only clear modified if no newer local edit happened while save was in flight.
                  if (latestLocalSeq === savedLocalSeq) {
                    latestData[latestIdx].modified = false;
                  }

                  // If it was new, reset to no longer indicate new
                  if (latestData[latestIdx].hasOwnProperty("new_record")) {
                    if (latestData[latestIdx].new_record === true) {
                      latestData[latestIdx][this.state.dataSetPrimaryKey] = result.id;
                      this.setState({ savingNewRecord: false });
                    }
                    latestData[latestIdx].new_record = false;
                  }
                }

                data = latestData;
                this.latestEditedData = latestData;

              } else {
                this.setState({ errorMessage: result.error_message });
                this.setState({ showAlert: true });
                return;
              }
            }
          } //validate
          else {
            this.setState({ errorMessage: validation[1] });
            this.setState({ showAlert: true });
          }
        }
      }
    } //for loop
    if (data[0].hasOwnProperty("sort_order")) {
      data.sort(function (a, b) {
        return a.sort_order - b.sort_order;
      });
    }
    this.latestEditedData = data;
    this.props.updateTableState(data, this.state.dataSet);
  };

  handleBatchSave = async (cellInfo, event) => {
    this.setState({ showAlert: false });
    let data = [...this.props.data];

    let body = [];
    let batches = [];

    // Iterate through each record - we need to find a way to speed this up
    for (var i = 0; i < data.length; i++) {
      const validation = this.props.validateRelatedRecord(data[i], this.state.dataSet);

      // Check if modified
      if (data[i].hasOwnProperty("modified")) {
        // Check if modified
        if (data[i].modified === true) {
          // Check if valid
          if (validation[0] === true) {
            //Save the data
            if (!this.state.savingNewRecord) {
              // Check if new record
              if (data[i].hasOwnProperty("new_record")) {
                if (data[i].new_record === true) {
                  this.setState({ savingNewRecord: true });
                }
              }

              body.push((this.state.dataSet, data[i]));
            }
          } else {
            this.setState({ errorMessage: validation[1] });
            this.setState({ showAlert: true });
          }
        }
      }

      if (body.length === 50 || i === data.length - 1) {
        batches.push(body);
        body = [];
      }
    } //for loop

    this.setState({ isLoading: true });
    let results = [];
    for (i = 0; i < batches.length; i++) {
      results[i] = await this.handleBatchDataSave(batches[i]);
    }
    this.setState({ isLoading: false });

    // Reset state to no longer modified
    for (i = 0; i < batches.length; i++) {
      if (results[i]) {
        let body = batches[i];
        for (var j = 0; j < body.length; j++) {
          body[j].modified = false;
        }
      } else {
        this.setState({ errorMessage: results[i].error_message });
        this.setState({ showAlert: true });
        return;
      }
    }

    if (data[0].hasOwnProperty("sort_order")) {
      data.sort(function (a, b) {
        return a.sort_order - b.sort_order;
      });
    }
    this.props.updateTableState(data, this.state.dataSet);
  };

  async handleBatchDataSave(data) {
    try {
      const saveLeaseRecord = this.props.saveLeaseRecord || this.props.saveRecord;
      return await saveLeaseRecord(data, this.state.dataSet + "_batch", this.state.dataSetPrimaryKey);
    } catch (e) {
      alert("Error saving");
    }
  }

  renderEditable = (cellInfo) => {
    const selectMenuProps = {
      menuPortalTarget: typeof document !== "undefined" ? document.body : undefined,
      menuPosition: "fixed",
      styles: {
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
      },
    };

    let rowData = this.state.data[cellInfo.index] || this.props.data[cellInfo.index] || {};
    if (Array.isArray(this.latestEditedData)) {
      const editedRow = this.latestEditedData.find((row) => row.lease_id === cellInfo.original.lease_id);
      if (editedRow) {
        rowData = editedRow;
      }
    }
    const cellValue = rowData[cellInfo.column.id];

    if (cellInfo.column.id === "area") {
      return (
        <input
          onChange={this.handleNumberChange.bind(null, cellInfo)}
          onFocus={this.handleCellFocus}
          onBlur={this.handleCellBlur.bind(null, cellInfo)}
          type="number"
          className="no-border form-control"
          value={cellValue != null ? cellValue : ""}
        />
      );
    }

    if (cellInfo.column.id === "floor_type") {
      if (cellValue) {
        return (
          <div>
            <Select
              value={{ value: cellValue, label: cellValue }}
              onChange={this.handleDropdownChange.bind(null, cellInfo)}
              options={this.props.floorTypeOptions}
              onFocus={this.handleCellFocus}
              className="no-border"
              {...selectMenuProps}
            />
          </div>
        );
      } else {
        return (
          <div>
            <Select
              value={{ value: null, label: null }}
              onChange={this.handleDropdownChange.bind(null, cellInfo)}
              options={this.props.floorTypeOptions}
              onFocus={this.handleCellFocus}
              className="no-border"
              {...selectMenuProps}
            />
          </div>
        );
      }
    }

    if (cellInfo.column.id === "classification_code") {
      if (cellValue) {
        let label = "";
        try {
          label = this.props.leaseClassificationOptions.find((o) => o.value === cellValue).label;
        } catch {
          label = cellValue;
        }

        return (
          <div>
            <Select
              value={{ value: cellValue, label: label }}
              onChange={this.handleDropdownChange.bind(null, cellInfo)}
              options={this.props.leaseClassificationOptions}
              onFocus={this.handleCellFocus}
              className="no-border"
              {...selectMenuProps}
            />
          </div>
        );
      } else {
        return (
          <div>
            <Select
              value={{ value: null, label: null }}
              onChange={this.handleDropdownChange.bind(null, cellInfo)}
              options={this.props.leaseClassificationOptions}
              onFocus={this.handleCellFocus}
              className="no-border"
              {...selectMenuProps}
            />
          </div>
        );
      }
    }

    if (cellInfo.column.id === "building_name") {
      if (cellValue) {
        return (
          <div>
            <Select
              value={{ value: cellValue, label: cellValue }}
              onChange={this.handleDropdownChange.bind(null, cellInfo)}
              options={this.state.buildingNameOptions}
              onFocus={this.handleCellFocus}
              onBlur={this.handleCellBlur.bind(null, cellInfo)}
              className="no-border"
              isClearable
              {...selectMenuProps}
            />
          </div>
        );
      } else {
        return (
          <div>
            <Select
              value={{ value: null, label: null }}
              onChange={this.handleDropdownChange.bind(null, cellInfo)}
              options={this.state.buildingNameOptions}
              onFocus={this.handleCellFocus}
              onBlur={this.handleCellBlur.bind(null, cellInfo)}
              className="no-border"
              {...selectMenuProps}
            />
          </div>
        );
      }
    }

    if (cellInfo.column.id === "lessee_trading") {
      return (
        <input
          onChange={this.handleInputChange.bind(null, cellInfo)}
          onBlur={this.handleLesseeTradingBlur.bind(null, cellInfo)}
          onFocus={this.handleCellFocus}
          type="text"
          className="no-border form-control"
          value={cellValue != null ? cellValue : ""}
          disabled={cellInfo.original.led_head_office_lease_id && cellInfo.column.id === "lessee_trading" ? true : false}
        />
      );
    }

    switch (typeof cellValue) {
      case "boolean":
        if (cellInfo.column.id === "selected") {
          return (
            <Form.Check
              checked={cellValue}
              onChange={this.handleCheckboxChange.bind(null, cellInfo)}
              label=""
              type="checkbox"
              style={{
                textAlign: "center",
              }}
            />
          );
        }
        if (["still_an_occ", "include_cs_text", "registered"].indexOf(cellInfo.column.id) >= 0) {
          return (
            <div style={{ display: "flex", justifyContent: "center" }}>
              <input
                type="checkbox"
                checked={Boolean(cellValue)}
                onChange={this.handleCheckboxChange.bind(null, cellInfo)}
                onFocus={this.handleCellFocus}
                onBlur={this.handleCellBlur.bind(null, cellInfo)}
              />
            </div>
          );
        }
        return (
          <Form.Check
            checked={cellValue}
            onChange={this.handleCheckboxChange.bind(null, cellInfo)}
            label=""
            type="checkbox"
            onFocus={this.handleCellFocus}
            onBlur={this.handleCellBlur.bind(null, cellInfo)}
            style={{
              textAlign: "center",
            }}
          />
        );

      case "number":
        return (
          <input
            onChange={this.handleNumberChange.bind(null, cellInfo)}
            onFocus={this.handleCellFocus}
            onBlur={this.handleCellBlur.bind(null, cellInfo)}
            type="number"
            className="no-border form-control"
            value={cellValue != null ? cellValue : " "}
          />
        );

      default:
        if (cellInfo.column.id.indexOf("date") >= 0) {
          if (cellInfo.column.id === 'expiry_date') {
            return (
                <DatePicker
                selected={parseDate(cellValue)}
                    onChange={this.handleDateChange.bind(null, cellInfo)}
                    onFocus={this.handleCellFocus}
                    onBlur={this.handleCellBlur.bind(null, cellInfo)}
                    dateFormat="dd/MM/yyyy"
                    className={`no-border form-control ${isDatePastOrPresent(cellValue)? 'expired_lease' : ''}`}
                />
            );
          } else {
            return (
                <DatePicker
                selected={parseDate(cellValue)}
                    onChange={this.handleDateChange.bind(null, cellInfo)}
                    onFocus={this.handleCellFocus}
                    onBlur={this.handleCellBlur.bind(null, cellInfo)}
                    dateFormat="dd/MM/yyyy"
                    className="no-border form-control"
                />
            );
          }
        } else {
          return (
            <input
              onChange={this.handleInputChange.bind(null, cellInfo)}
              onFocus={this.handleCellFocus}
              onBlur={this.handleCellBlur.bind(null, cellInfo)}
              type="text"
              className="no-border form-control"
              value={cellValue != null ? cellValue : ""}
              disabled={cellInfo.original.led_head_office_lease_id && cellInfo.column.id === "lessee_trading" ? true : false}
            />
          );
        }
    }
  };

  applyFilters() {
    let data = [];
    const oldData = Array.isArray(this.latestEditedData) ? [...this.latestEditedData] : [...this.props.data];
    var i;

    for (i = 0; i < oldData.length; i++) {
      if (!this.state.stillAnOccupantFilter || !this.state.archiveFilter) {
        //Both filters applied
        if (!this.state.stillAnOccupantFilter && !this.state.archiveFilter) {
          if (oldData[i].hasOwnProperty("archived") && oldData[i].hasOwnProperty("still_an_occ")) {
            if ((!oldData[i].archived || oldData[i].archived === null) && oldData[i].still_an_occ && !oldData[i].archived_date) {
              data.push(oldData[i]);
            }
          }
        } else if (!this.state.stillAnOccupantFilter) {
          if (oldData[i].hasOwnProperty("still_an_occ")) {
            if (oldData[i].still_an_occ && !oldData[i].archived_date) {
              data.push(oldData[i]);
            }
          }
        } else if (!this.state.archiveFilter) {
          // console.log("Not archived");
          if (oldData[i].hasOwnProperty("archived")) {
            if ((!oldData[i].archived || oldData[i].archived === null) && !oldData[i].archived_date) {
              data.push(oldData[i]);
            }
          }
        }
      } else {
        data.push(oldData[i]);
      }
    }

    this.setState({ data }, this.sortData);
  }

  handleStillAnOccupantFilterChange = (event) => {
    this.setState({ stillAnOccupantFilter: event.target.checked }, this.applyFilters);
  };

  handleArchiveFilterChange = async (event) => {
    this.setState(
      {
        archiveFilter: event.target.checked,
      },
      this.applyFilters
    );
  };

  handleSortOptionChange = (selectedSortOption) => {
    this.setState({ selectedSortOption }, this.sortData);
  };

  handlePropertyBuildingNameChange = (selectedPropertyBuildingName) => {
    if (selectedPropertyBuildingName) {
      this.setState({ selectedPropertyBuildingName });
    } else {
      this.setState({ selectedPropertyBuildingName: { value: null, label: null } });
    }
  };
  handleTenantBuildingNameChange = (selectedTenantBuildingName) => {
    if (selectedTenantBuildingName) {
      this.setState({ selectedTenantBuildingName });
    } else {
      this.setState({ selectedTenantBuildingName: { value: null, label: null } });
    }
  };
  handleUpdateAllBuildings = async (event) => {
    let data = [...this.props.data];
    const confirm = window.confirm("Are you sure you want to update all matching building names?");
    if (!confirm) {
      return;
    }

    for (var i = 0; i < data.length; i++) {
      // if (data[i].building_name){
      if (data[i].building_name === this.state.selectedTenantBuildingName.value) {
        if (!this.state.archiveFilter && data[i].archived) {
          continue;
        }
        if (!this.state.stillAnOccupantFilter && !data[i].still_an_occ) {
          continue;
        }

        data[i].building_name = this.state.selectedPropertyBuildingName.value;
        data[i].modified = true;
      }
      // }
    }
    this.props.updateTableState(data, this.state.dataSet);
    await this.handleBatchSave(null, null);
    alert("Update complete");
  };

  handleUpdateSelectedBuildings = async (event) => {
    let data = [...this.props.data];
    const confirm = window.confirm("Are you sure you want to update selected building names?");
    if (!confirm) {
      return;
    }

    for (var i = 0; i < data.length; i++) {
      if (!data[i].selected) {
        continue;
      }

      data[i].building_name = this.state.selectedPropertyBuildingName.value;
      data[i].modified = true;
    }
    this.props.updateTableState(data, this.state.dataSet);
    await this.handleBatchSave(null, null);
    alert("Update complete");
  };

  handleMerge = async (event) => {
    //check one record has registered lessee = null, lessee trading not null, archived = no, deleted = no
    //check one record has registered lessee not null, lessee trading = null, archived = no, deleted = no
    const registered_lease = this.state.data.filter((f) => f.selected && f.lessee_registered && !f.lessee_trading && !f.archived)[0];
    const tenant = this.state.data.filter((f) => f.selected && !f.lessee_registered && f.lessee_trading && !f.archived)[0];
    if (!registered_lease || !tenant) {
      alert("The selected records do not meet the criteria for merging. One record must be a Lease record without a Lessee Trading and the one must be a Tenant record (no Registered Lessee).");
      return;
    }
    registered_lease.lessee_registered_checked = true;
    tenant.lessee_trading_checked = true;

    const keys = Object.keys(registered_lease);
    for (const key of keys) {
      if (registered_lease[key] && key !== "lessee_registered" && key !== "lessee_trading") {
        registered_lease[key + "_checked"] = true;
        tenant[key + "_checked"] = false;
      } else if (tenant[key] && key !== "lessee_registered" && key !== "lessee_trading") {
        registered_lease[key + "_checked"] = false;
        tenant[key + "_checked"] = true;
      }
    }
    if (tenant.led_head_office_lease_id) {
      registered_lease["telephone_checked"] = false;
      registered_lease["fax_checked"] = false;
      registered_lease["weburl_checked"] = false;
      registered_lease["email_checked"] = false;
      registered_lease["address_line_1_checked"] = false;
      registered_lease["address_line_2_checked"] = false;
      registered_lease["suburb_checked"] = false;
      registered_lease["state_checked"] = false;
      registered_lease["postcode_checked"] = false;

      tenant["telephone_checked"] = true;
      tenant["fax_checked"] = true;
      tenant["weburl_checked"] = true;
      tenant["email_checked"] = true;
      tenant["address_line_1_checked"] = true;
      tenant["address_line_2_checked"] = true;
      tenant["suburb_checked"] = true;
      tenant["state_checked"] = true;
      tenant["postcode_checked"] = true;
    }

    this.setState({ showMergeDialog: true, registered_lease, tenant });
  };
  saveHeadOfficeLease(headOfficeLease) {
    return post({
      apiName: "colin",
      path: `/headoffices/${headOfficeLease.led_head_office_id}/headofficeleases/new`,
      options: {
        body: headOfficeLease,
      },
    }).response.then(({ body }) => body.json());
  }

  handleExecuteMerge = async (event) => {
    const lessee_registered_index = this.state.data.findIndex((f) => f.lease_id === this.state.registered_lease.lease_id);
    const tenant_index = this.state.data.findIndex((f) => f.lease_id === this.state.tenant.lease_id);

    let data = [...this.state.data];
    const confirm = window.confirm(`Are you sure you want to merge Lease record for ${this.state.data[lessee_registered_index].lessee_registered} with Tenant record for ${this.state.data[tenant_index].lessee_trading} retaining the values as selected below?`);

    if (!confirm) {
      return;
    }
    const keys = Object.keys(this.state.registered_lease);
    for (const key of keys) {
      if (this.state.tenant[key + "_checked"]) {
        data[lessee_registered_index][key] = this.state.tenant[key];
      }
    }
    data[lessee_registered_index].modified = true;
    data[tenant_index].archived_date = new Date();
    data[tenant_index].modified = true;
    data[lessee_registered_index].selected = false;
    data[tenant_index].selected = false;
    if (this.state.tenant.led_head_office_id) {
      data[lessee_registered_index].led_head_office_id = this.state.tenant.led_head_office_id;
      this.saveHeadOfficeLease({ led_head_office_id: this.state.tenant.led_head_office_id, tenant_name: this.state.tenant.lessee_trading, lease_id: data[lessee_registered_index].lease_id, created_by: this.props.user });
    }
    this.props.updateTableState(data, this.state.dataSet);
    this.setState({ showMergeDialog: false, data }, this.handleSave);
    this.applyFilters();
    alert("Tenant/Lease merged completed successfully.");
  };

  sortData() {
    let old_data = [...this.state.data];

    if (this.state.selectedSortOption.value === "Level No.") {
      let sort_1 = old_data.sort(function (a, b) {
        if (a.lessee_registered != null && b.lessee_registered === null) {
          return -1;
        }
        if (a.lessee_registered === null && b.lessee_registered != null) {
          return 1;
        }
        if (a.lessee_registered < b.lessee_registered) {
          return -1;
        }
        if (a.lessee_registered > b.lessee_registered) {
          return 1;
        }
        return 0;
      });

      let sort_2 = sort_1.sort(function (a, b) {
        if (a.lessee_trading != null && b.lessee_trading === null) {
          return -1;
        }
        if (a.lessee_trading === null && b.lessee_trading != null) {
          return 1;
        }
        if (a.lessee_trading < b.lessee_trading) {
          return -1;
        }
        if (a.lessee_trading > b.lessee_trading) {
          return 1;
        }
        return 0;
      });

      let sort_3 = sort_2.sort(function (a, b) {
        if (a.suite !== null && b.suite === null) {
          return -1;
        }
        if (a.suite === null && b.suite !== null) {
          return 1;
        }
        if (a.suite < b.suite) {
          return -1;
        }
        if (a.suite > b.suite) {
          return 1;
        }
        return 0;
      });

      let sort_4 = sort_3.sort(function (a, b) {
        if (a.level_number !== null && b.level_number === null) {
          return -1;
        }
        if (a.level_number === null && b.level_number !== null) {
          return 1;
        }
        if (a.level_number < b.level_number) {
          return -1;
        }
        if (a.level_number > b.level_number) {
          return 1;
        }
        return 0;
      });

      let sort_5 = sort_4.sort(function (a, b) {
        if (a.building_name !== null && b.building_name === null) {
          return -1;
        }
        if (a.building_name === null && b.building_name !== null) {
          return 1;
        }
        if (a.building_name < b.building_name) {
          return -1;
        }
        if (a.building_name > b.building_name) {
          return 1;
        }
        return 0;
      });

      this.setState({ sort_5 }, () => this.renderTable(sort_5));
    } else {
      let data = old_data.sort(function (a, b) {
        if (a.lessee_trading != null && b.lessee_trading === null) {
          return -1;
        }
        if (a.lessee_trading === null && b.lessee_trading != null) {
          return 1;
        }
        if (a.lessee_trading < b.lessee_trading) {
          return -1;
        }
        if (a.lessee_trading > b.lessee_trading) {
          return 1;
        }
        return 0;
      });
      this.setState({ data }, () => this.renderTable(data));
    }
  }

  getTrProps = (state, rowInfo, instance) => {
    if (rowInfo) {
      if (rowInfo.row.archived) {
        return {
          style: {
            background: "#fafafa",
            color: "white",
          },
        };
      }
    }
    return {};
  };

  getTableColumns() {
    if (!this.tableColumns) {
      this.tableColumns = [
        {
          id: "selected",
          Header: "Selected",
          maxWidth: 60,
          accessor: "selected",
          Cell: this.renderEditable,
          style: { textAlign: "center" },
        },
        {
          id: "lease_id",
          Header: "ID",
          maxWidth: 60,
          accessor: "lease_id",
          Cell: (e) => (
            <Link
              to={`/properties/${e.original.property_key}/leases/${e.value}`}
              state={{
                data: (this.state.data.length > 0 ? this.state.data : this.props.data || []).map((a) => a.lease_id),
                propertyidlist: this.props.idList,
              }}
            >
              {e.value}
            </Link>
          ),
        },
        {
          id: "building_name",
          Header: "Building",
          accessor: "building_name",
          maxWidth: 200,
          Cell: this.renderEditable,
        },
        {
          id: "level_number",
          Header: "Level Number (Numeric)",
          accessor: "level_number",
          maxWidth: 80,
          Cell: this.renderEditable,
        },
        {
          id: "level_desc",
          Header: "Level Description",
          accessor: "level_desc",
          maxWidth: 100,
          Cell: this.renderEditable,
        },
        {
          id: "suite",
          Header: "Suite",
          accessor: "suite",
          maxWidth: 100,
          Cell: this.renderEditable,
        },
        {
          id: "area",
          Header: "Area",
          accessor: "area",
          maxWidth: 80,
          Cell: this.renderEditable,
        },
        {
          id: "lessee_registered",
          Header: "Registered Lessee",
          accessor: "lessee_registered",
          maxWidth: 110,
          Cell: this.renderEditable,
        },
        {
          id: "lessee_trading",
          Header: "Lessee Trading",
          accessor: "lessee_trading",
          maxWidth: 100,
          Cell: this.renderEditable,
        },
        {
          id: "still_an_occ",
          Header: "Still Here",
          accessor: "still_an_occ",
          maxWidth: 80,
          Cell: this.renderEditable,
          style: { textAlign: "center" },
        },
        {
          id: "include_cs_text",
          Header: "Include Text",
          accessor: "include_cs_text",
          maxWidth: 80,
          Cell: this.renderEditable,
          style: { textAlign: "center" },
        },

        {
          id: "registered",
          Header: "Registered",
          accessor: "registered",
          maxWidth: 80,
          Cell: this.renderEditable,
          style: { textAlign: "center" },
        },
        {
          id: "floor_type",
          Header: "Floor Type",
          accessor: "floor_type",
          maxWidth: 150,
          Cell: this.renderEditable,
        },
        {
          id: "classification_code",
          Header: "Classification",
          accessor: "classification_code",
          Cell: this.renderEditable,
        },
        {
          id: "expiry_date",
          Header: "Expiry Date",
          accessor: "expiry_date",
          Cell: this.renderEditable,
          maxWidth: 100,
          style: { textAlign: "center" },
        },
        {
          id: "archived",
          Header: "Archived",
          accessor: "archived",
          maxWidth: 100,
          style: { textAlign: "center" },
          Cell: (e) => (
            <Form.Check
              checked={e.value}
              type="checkbox"
              disabled
            />
          ),
        },
      ];
    }

    return this.tableColumns;
  }

  renderTable(data) {
    const columns = this.getTableColumns();

    if (!data || !this.props.leaseClassificationOptions) {
      return null;
    } else {
      return (
        <div>
          <ReactTable
            data={data}
            columns={columns}
            defaultPageSize={100}
            sortable={true}
            getTrProps={this.getTrProps}
          />
        </div>
      );
    }
  }

  renderTable2(data) {
    const columns = [
      {
        id: "lease_id",
        Header: "ID",
        maxWidth: 60,
        accessor: "lease_id",
        Cell: (e) => (
          <Link
            to={`/properties/${e.original.property_key}/leases/${e.value}`}
            state={{
              data: (this.props.data || []).map((lease) => lease.lease_id),
              propertyidlist: this.props.idList,
            }}
          >
            {e.value}
          </Link>
        ),
      },
      {
        id: "property_key",
        Header: "Cityscope Ref",
        accessor: "property_key",
        Cell: (e) => (
          <a
            target="_blank"
            rel="noreferrer"
            href={`/properties/${e.original.property_key}`}
          >
            {e.value}
          </a>
        ),
        maxWidth: 300,
      },
      {
        id: "building_name",
        Header: "Building",
        accessor: "building_name",
      },
      {
        id: "lease_number",
        Header: "Lease No.",
        accessor: "lease_number",
        maxWidth: 300,
      },
      {
        id: "lessee_trading",
        Header: "Lessee Trading",
        accessor: "lessee_trading",
      },
      {
        id: "lessee_registered",
        Header: "Registered Lessee",
        accessor: "lessee_registered",
      },
      {
        id: "still_an_occ",
        Header: "Still Here",
        // accessor: "still_an_occ",
        maxWidth: 100,
        accessor: (d) => {
          if (d.still_an_occ) {
            return (
              <Form.Check
                checked={d.still_an_occ}
                label=""
                type="checkbox"
                disabled="True"
                style={{
                  textAlign: "center",
                }}
              />
            );
          } else {
            return null;
          }
        },
      },
      {
        id: "registered",
        Header: "Registered",
        accessor: "registered",
        maxWidth: 100,
        sortingFn: (rowA, rowB, columnId) => {
          const registeredA = rowA.getValue(columnId) === true ? 1 : 0;
          const registeredB = rowB.getValue(columnId) === true ? 1 : 0;
          return registeredA - registeredB;
        },
        Cell: (e) => {
          if (!e.value) {
            return null;
          }

          return (
            <Form.Check
              checked={e.value}
              label=""
              type="checkbox"
              disabled="True"
              style={{
                textAlign: "center",
              }}
            />
          );
        },
      },
      {
        id: "classification",
        Header: "Classification",
        accessor: "classification",
      },
      {
        id: "telephone",
        Header: "Telephone",
        accessor: "telephone",
        maxWidth: 100,
      },
      {
        id: "state",
        Header: "State",
        accessor: "state",
        maxWidth: 100,
      },
      {
        id: "expiry_date",
        Header: "Expiry Date",
        accessor: (d) => {
          if (d.expiry_date) {
            return Moment(d.expiry_date).local().format("DD-MM-YYYY");
          } else {
            return null;
          }
        },
        maxWidth: 120,
        style: { textAlign: "center" },
      },
      {
        id: "archived",
        Header: "Archived",
        // accessor: "archived",
        maxWidth: 100,
        accessor: (d) => {
          return (
            <Form.Check
              checked={d.archived}
              label=""
              type="checkbox"
              disabled="True"
              style={{
                textAlign: "center",
              }}
            />
          );
        },
      },
    ];

    if (!data) {
      return null;
    } else {
      return (
        <div>
          <ReactTable
            data={data}
            columns={columns}
            defaultPageSize={100}
            sortable={true}
          />
        </div>
      );
    }
  }

  handleCheckBoxChange = async (event) => {
    const name = event.target.name;
    let tenant = { ...this.state.tenant };
    let registered_lease = { ...this.state.registered_lease };

    if (name.startsWith("tenant")) {
      const clean_name = name.substring(7, name.length);
      registered_lease[clean_name] = !event.target.checked;
      tenant[clean_name] = event.target.checked;
      tenant.modified = true;
      registered_lease.modified = true;
      this.setState({ tenant, registered_lease });
    } else {
      const clean_name = name.substring(11, name.length);
      registered_lease[clean_name] = event.target.checked;
      tenant[clean_name] = !event.target.checked;
      tenant.modified = true;
      registered_lease.modified = true;
      this.setState({ tenant, registered_lease });
    }
  };

  renderComponent() {
    return (
      <div className="leases">
        {this.state.showHeadOfficeSearchDialog && (
          <HeadOfficeModal
            isAuthenticated={this.props.isAuthenticated}
            user={this.props.user}
            toggleHeadOfficeSearchDialog={this.toggleHeadOfficeSearchDialog}
            leaseid={this.state.lastLeaseIdSelected}
            show={this.state.showHeadOfficeSearchDialog}
            data={this.state.headOfficeMatches}
            updateTableState={this.updateTableState}
            saveRecord={this.saveHeadOfficeRecord}
          />
        )}
        <Modal
          show={this.state.showAlert}
          onHide={(e) => {
            this.setState({ showAlert: !this.state.showAlert });
          }}
          centered
        >
          <Modal.Header closeButton>
            {" "}
            <Modal.Title>Uh Oh!</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {" "}
            <Alert variant="danger">{this.state.errorMessage}</Alert>{" "}
          </Modal.Body>
        </Modal>

        {this.state.registered_lease && (
          <Modal
            dialogClassName="modal-90w"
            show={this.state.showMergeDialog}
            onHide={(e) => {
              this.setState({ showMergeDialog: !this.state.showMergeDialog });
            }}
            centered
          >
            <Modal.Header closeButton>
              {" "}
              <Modal.Title>Merge Tenant and Lease</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ "max-height": "calc(100vh - 210px)", "overflow-y": "auto" }}>
              <Form>
                <Row>
                  {/* Lease */}

                  <Col>
                    <Row>
                      <Col
                        sm={2}
                        md={2}
                        lg={2}
                      ></Col>
                      <Col
                        sm={6}
                        md={6}
                        lg={4}
                      >
                        <h5>KEEPING</h5>
                      </Col>
                      <Col></Col>
                      <Col></Col>
                      <Col
                        sm={6}
                        md={6}
                        lg={5}
                      >
                        <h5>BEING MERGED</h5>
                      </Col>
                    </Row>
                    <Row>
                      <Col
                        sm={2}
                        md={2}
                        lg={2}
                      ></Col>
                      <Col
                        sm={6}
                        md={6}
                        lg={4}
                      >
                        <h6>
                          <strong>Lease Record</strong>
                        </h6>
                      </Col>
                      <Col></Col>
                      <Col></Col>
                      <Col
                        sm={6}
                        md={6}
                        lg={5}
                      >
                        <h6>
                          <strong>Tenant Record</strong>
                        </h6>
                      </Col>
                    </Row>
                    <Row>
                      <Col
                        sm={6}
                        md={6}
                        lg={5}
                      >
                        <Form.Group
                          as={Row}
                          controlId="lessee_registered"
                          size="small"
                        >
                          <Form.Label
                            className="text-right"
                            column
                            sm={6}
                            md={6}
                            lg={4}
                          >
                            Lessee Registered
                          </Form.Label>
                          <Col>
                            <Form.Control
                              readOnly
                              value={this.state.registered_lease.lessee_registered}
                              as="input"
                              type="text"
                              size="small"
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                      <Col>
                        <Form.Group
                          as={Row}
                          controlId="lessee_registered_checked"
                          size="small"
                        >
                          <Col>
                            <Form.Check
                              checked={this.state.registered_lease.lessee_registered_checked}
                              onChange={this.handleCheckBoxChange}
                              name="registered_lessee_registered_checked"
                              type="checkbox"
                              disabled
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                      <Col className="text-right">
                        <Form.Group
                          as={Row}
                          controlId="lessee_registered_checked"
                          size="small"
                        >
                          <Col>
                            <Form.Check
                              checked={this.state.tenant.lessee_registered_checked}
                              onChange={this.handleCheckBoxChange}
                              name="tenant_lessee_registered_checked"
                              type="checkbox"
                              disabled
                            />
                          </Col>
                        </Form.Group>
                      </Col>

                      <Col
                        sm={6}
                        md={6}
                        lg={5}
                      >
                        <Form.Group
                          as={Row}
                          controlId="lessee_registered"
                          size="small"
                        >
                          <Col>
                            <Form.Control
                              readOnly
                              value={this.state.tenant.lessee_registered}
                              as="input"
                              type="text"
                              size="small"
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col
                        sm={6}
                        md={6}
                        lg={5}
                      >
                        <Form.Group
                          as={Row}
                          controlId="acn"
                          size="small"
                        >
                          <Form.Label
                            className="text-right"
                            column
                            sm={6}
                            md={6}
                            lg={4}
                          >
                            ACN
                          </Form.Label>
                          <Col>
                            <Form.Control
                              readOnly
                              value={this.state.registered_lease.acn}
                              as="input"
                              type="text"
                              size="small"
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                      <Col>
                        <Form.Group
                          as={Row}
                          controlId="lease_acn_checked"
                          size="small"
                        >
                          <Col>
                            <Form.Check
                              checked={this.state.registered_lease.acn_checked}
                              onChange={this.handleCheckBoxChange}
                              name="registered_acn_checked"
                              type="checkbox"
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                      <Col className="text-right">
                        <Form.Group
                          as={Row}
                          controlId="acn_checked"
                          size="small"
                        >
                          <Col>
                            <Form.Check
                              checked={this.state.tenant.acn_checked}
                              onChange={this.handleCheckBoxChange}
                              name="tenant_acn_checked"
                              type="checkbox"
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                      <Col
                        sm={6}
                        md={6}
                        lg={5}
                      >
                        <Form.Group
                          as={Row}
                          controlId="acn"
                          size="small"
                        >
                          <Col>
                            <Form.Control
                              readOnly
                              value={this.state.tenant.acn}
                              as="input"
                              type="text"
                              size="small"
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col
                        sm={6}
                        md={6}
                        lg={5}
                      >
                        <Form.Group
                          as={Row}
                          controlId="lessee_trading"
                          size="small"
                        >
                          <Form.Label
                            className="text-right"
                            column
                            sm={6}
                            md={6}
                            lg={4}
                          >
                            Lessee Trading
                          </Form.Label>
                          <Col>
                            <Form.Control
                              readOnly
                              value={this.state.registered_lease.lessee_trading}
                              as="input"
                              type="text"
                              size="small"
                            />
                          </Col>
                        </Form.Group>
                      </Col>

                      <Col>
                        <Form.Group
                          as={Row}
                          controlId="lessee_trading_checked"
                          size="small"
                        >
                          <Col>
                            <Form.Check
                              checked={this.state.registered_lease.lessee_trading_checked}
                              onChange={this.handleCheckBoxChange}
                              name="registered_lessee_trading_checked"
                              type="checkbox"
                              disabled
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                      <Col className="text-right">
                        <Form.Group
                          as={Row}
                          controlId="lessee_trading_checked"
                          size="small"
                        >
                          <Col>
                            <Form.Check
                              checked={this.state.tenant.lessee_trading_checked}
                              onChange={this.handleCheckBoxChange}
                              name="tenant_lessee_trading_checked"
                              type="checkbox"
                              disabled
                            />
                          </Col>
                        </Form.Group>
                      </Col>

                      <Col
                        sm={6}
                        md={6}
                        lg={5}
                      >
                        <Form.Group
                          as={Row}
                          controlId="lessee_trading"
                          size="small"
                        >
                          <Col>
                            <Form.Control
                              readOnly
                              value={this.state.tenant.lessee_trading}
                              as="input"
                              type="text"
                              size="small"
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col
                        sm={6}
                        md={6}
                        lg={5}
                      >
                        <Form.Group
                          as={Row}
                          controlId="classification_code"
                          size="small"
                        >
                          <Form.Label
                            className="text-right"
                            column
                            sm={6}
                            md={6}
                            lg={4}
                          >
                            Broad Type
                          </Form.Label>
                          <Col>
                            <Form.Control
                              readOnly
                              value={this.state.registered_lease.classification}
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                      <Col>
                        <Form.Group
                          as={Row}
                          controlId="classification_code_checked"
                          size="small"
                        >
                          <Col>
                            <Form.Check
                              checked={this.state.registered_lease.classification_code_checked}
                              onChange={this.handleCheckBoxChange}
                              name="registered_classification_code_checked"
                              type="checkbox"
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                      <Col className="text-right">
                        <Form.Group
                          as={Row}
                          controlId="classification_code_checked"
                          size="small"
                        >
                          <Col>
                            <Form.Check
                              checked={this.state.tenant.classification_code_checked}
                              onChange={this.handleCheckBoxChange}
                              name="tenant_classification_code_checked"
                              type="checkbox"
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                      <Col
                        sm={6}
                        md={6}
                        lg={5}
                      >
                        <Form.Group
                          as={Row}
                          controlId="classification_code"
                          size="small"
                        >
                          <Col>
                            <Form.Control
                              readOnly
                              value={this.state.tenant.classification}
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col
                        sm={6}
                        md={6}
                        lg={5}
                      >
                        <Form.Group
                          as={Row}
                          controlId="still_an_occ"
                          size="small"
                        >
                          <Form.Label
                            className="text-right"
                            column
                            sm={6}
                            md={6}
                            lg={4}
                          >
                            Still Here
                          </Form.Label>
                          <Col>
                            <Form.Check
                              readOnly
                              checked={this.state.registered_lease.still_an_occ}
                              name="registered_still_an_occ"
                              type="checkbox"
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                      <Col>
                        <Form.Group
                          as={Row}
                          controlId="still_an_occ_checked"
                          size="small"
                        >
                          <Col>
                            <Form.Check
                              checked={this.state.registered_lease.still_an_occ_checked}
                              onChange={this.handleCheckBoxChange}
                              name="registered_still_an_occ_checked"
                              type="checkbox"
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                      <Col className="text-right">
                        <Form.Group
                          as={Row}
                          controlId="still_an_occ_checked"
                          size="small"
                        >
                          <Col>
                            <Form.Check
                              checked={this.state.tenant.still_an_occ_checked}
                              onChange={this.handleCheckBoxChange}
                              name="tenant_still_an_occ_checked"
                              type="checkbox"
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                      <Col
                        sm={6}
                        md={6}
                        lg={5}
                      >
                        <Form.Group
                          as={Row}
                          controlId="still_an_occ"
                          size="small"
                        >
                          <Col>
                            <Form.Check
                              readOnly
                              checked={this.state.tenant.still_an_occ}
                              name="tenant_tenant_still_an_occ"
                              type="checkbox"
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col
                        sm={6}
                        md={6}
                        lg={5}
                      >
                        <Form.Group
                          as={Row}
                          controlId="include_cs_text"
                          size="small"
                        >
                          <Form.Label
                            className="text-right"
                            column
                            sm={6}
                            md={6}
                            lg={4}
                          >
                            Include in Text
                          </Form.Label>
                          <Col>
                            <Form.Check
                              readOnly
                              checked={this.state.registered_lease.include_cs_text}
                              name="registered_include_cs_text"
                              type="checkbox"
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                      <Col>
                        <Form.Group
                          as={Row}
                          controlId="include_cs_text_checked"
                          size="small"
                        >
                          <Col>
                            <Form.Check
                              checked={this.state.registered_lease.include_cs_text_checked}
                              onChange={this.handleCheckBoxChange}
                              name="registered_include_cs_text_checked"
                              type="checkbox"
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                      <Col className="text-right">
                        <Form.Group
                          as={Row}
                          controlId="include_cs_text_checked"
                          size="small"
                        >
                          <Col>
                            <Form.Check
                              checked={this.state.tenant.include_cs_text_checked}
                              onChange={this.handleCheckBoxChange}
                              name="tenant_include_cs_text_checked"
                              type="checkbox"
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                      <Col
                        sm={6}
                        md={6}
                        lg={5}
                      >
                        <Form.Group
                          as={Row}
                          controlId="include_cs_text"
                          size="small"
                        >
                          <Col>
                            <Form.Check
                              readOnly
                              checked={this.state.tenant.include_cs_text}
                              name="tenant_include_cs_text"
                              type="checkbox"
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col
                        sm={6}
                        md={6}
                        lg={5}
                      >
                        <Form.Group
                          as={Row}
                          controlId="building_name"
                          size="small"
                        >
                          <Form.Label
                            className="text-right"
                            column
                            sm={6}
                            md={6}
                            lg={4}
                          >
                            Building Name
                          </Form.Label>
                          <Col>
                            <Form.Control
                              readOnly
                              value={this.state.registered_lease.building_name}
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                      <Col>
                        <Form.Group
                          as={Row}
                          controlId="building_name_checked"
                          size="small"
                        >
                          <Col>
                            <Form.Check
                              checked={this.state.registered_lease.building_name_checked}
                              onChange={this.handleCheckBoxChange}
                              name="registered_building_name_checked"
                              type="checkbox"
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                      <Col className="text-right">
                        <Form.Group
                          as={Row}
                          controlId="building_name_checked"
                          size="small"
                        >
                          <Col>
                            <Form.Check
                              checked={this.state.tenant.building_name_checked}
                              onChange={this.handleCheckBoxChange}
                              name="tenant_building_name_checked"
                              type="checkbox"
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                      <Col
                        sm={6}
                        md={6}
                        lg={5}
                      >
                        <Form.Group
                          as={Row}
                          controlId="building_name"
                          size="small"
                        >
                          <Col>
                            <Form.Control
                              readOnly
                              value={this.state.tenant.building_name}
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col
                        sm={6}
                        md={6}
                        lg={5}
                      >
                        <Form.Group
                          as={Row}
                          controlId="fronting"
                          size="small"
                        >
                          <Form.Label
                            className="text-right"
                            column
                            sm={6}
                            md={6}
                            lg={4}
                          >
                            Fronting
                          </Form.Label>
                          <Col>
                            <Form.Control
                              readOnly
                              value={this.state.registered_lease.fronting}
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                      <Col>
                        <Form.Group
                          as={Row}
                          controlId="fronting_checked"
                          size="small"
                        >
                          <Col>
                            <Form.Check
                              checked={this.state.registered_lease.fronting_checked}
                              onChange={this.handleCheckBoxChange}
                              name="registered_fronting_checked"
                              type="checkbox"
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                      <Col className="text-right">
                        <Form.Group
                          as={Row}
                          controlId="fronting_checked"
                          size="small"
                        >
                          <Col>
                            <Form.Check
                              checked={this.state.tenant.fronting_checked}
                              onChange={this.handleCheckBoxChange}
                              name="tenant_fronting_checked"
                              type="checkbox"
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                      <Col
                        sm={6}
                        md={6}
                        lg={5}
                      >
                        <Form.Group
                          as={Row}
                          controlId="fronting"
                          size="small"
                        >
                          <Col>
                            <Form.Control
                              readOnly
                              value={this.state.tenant.fronting}
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col
                        sm={6}
                        md={6}
                        lg={5}
                      >
                        <Form.Group
                          as={Row}
                          controlId="suite"
                          size="small"
                        >
                          <Form.Label
                            className="text-right"
                            column
                            sm={6}
                            md={6}
                            lg={4}
                          >
                            Suite
                          </Form.Label>
                          <Col>
                            <Form.Control
                              readOnly
                              value={this.state.registered_lease.suite}
                              as="input"
                              type="text"
                              size="small"
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                      <Col>
                        <Form.Group
                          as={Row}
                          controlId="suite_checked"
                          size="small"
                        >
                          <Col>
                            <Form.Check
                              checked={this.state.registered_lease.suite_checked}
                              onChange={this.handleCheckBoxChange}
                              name="registered_suite_checked"
                              type="checkbox"
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                      <Col className="text-right">
                        <Form.Group
                          as={Row}
                          controlId="suite_checked"
                          size="small"
                        >
                          <Col>
                            <Form.Check
                              checked={this.state.tenant.suite_checked}
                              onChange={this.handleCheckBoxChange}
                              name="tenant_suite_checked"
                              type="checkbox"
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                      <Col
                        sm={6}
                        md={6}
                        lg={5}
                      >
                        <Form.Group
                          as={Row}
                          controlId="suite"
                          size="small"
                        >
                          <Col>
                            <Form.Control
                              readOnly
                              value={this.state.tenant.suite}
                              as="input"
                              type="text"
                              size="small"
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col
                        sm={6}
                        md={6}
                        lg={5}
                      >
                        <Form.Group
                          as={Row}
                          controlId="level_desc"
                          size="small"
                        >
                          <Form.Label
                            className="text-right"
                            column
                            sm={6}
                            md={6}
                            lg={4}
                          >
                            Level Desc
                          </Form.Label>
                          <Col>
                            <Form.Control
                              readOnly
                              value={this.state.registered_lease.level_desc}
                              as="input"
                              type="text"
                              size="small"
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                      <Col>
                        <Form.Group
                          as={Row}
                          controlId="level_desc_checked"
                          size="small"
                        >
                          <Col>
                            <Form.Check
                              checked={this.state.registered_lease.level_desc_checked}
                              onChange={this.handleCheckBoxChange}
                              name="registered_level_desc_checked"
                              type="checkbox"
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                      <Col className="text-right">
                        <Form.Group
                          as={Row}
                          controlId="level_desc_checked"
                          size="small"
                        >
                          <Col>
                            <Form.Check
                              checked={this.state.tenant.level_desc_checked}
                              onChange={this.handleCheckBoxChange}
                              name="tenant_level_desc_checked"
                              type="checkbox"
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                      <Col
                        sm={6}
                        md={6}
                        lg={5}
                      >
                        <Form.Group
                          as={Row}
                          controlId="level_desc"
                          size="small"
                        >
                          <Col>
                            <Form.Control
                              readOnly
                              value={this.state.tenant.level_desc}
                              as="input"
                              type="text"
                              size="small"
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col
                        sm={6}
                        md={6}
                        lg={5}
                      >
                        <Form.Group
                          as={Row}
                          controlId="extra_desc"
                          size="small"
                        >
                          <Form.Label
                            className="text-right"
                            column
                            sm={6}
                            md={6}
                            lg={4}
                          >
                            Extra Desc
                          </Form.Label>
                          <Col>
                            <Form.Control
                              readOnly
                              onChange={this.handleChange}
                              value={this.state.registered_lease.extra_desc}
                              as="input"
                              type="text"
                              size="small"
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                      <Col>
                        <Form.Group
                          as={Row}
                          controlId="extra_desc_checked"
                          size="small"
                        >
                          <Col>
                            <Form.Check
                              checked={this.state.registered_lease.extra_desc_checked}
                              onChange={this.handleCheckBoxChange}
                              name="registered_extra_desc_checked"
                              type="checkbox"
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                      <Col className="text-right">
                        <Form.Group
                          as={Row}
                          controlId="extra_desc_checked"
                          size="small"
                        >
                          <Col>
                            <Form.Check
                              checked={this.state.tenant.extra_desc_checked}
                              onChange={this.handleCheckBoxChange}
                              name="tenant_extra_desc_checked"
                              type="checkbox"
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                      <Col
                        sm={6}
                        md={6}
                        lg={5}
                      >
                        <Form.Group
                          as={Row}
                          controlId="extra_desc"
                          size="small"
                        >
                          <Col>
                            <Form.Control
                              readOnly
                              onChange={this.handleChange}
                              value={this.state.tenant.extra_desc}
                              as="input"
                              type="text"
                              size="small"
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col
                        sm={6}
                        md={6}
                        lg={5}
                      >
                        <Form.Group
                          as={Row}
                          controlId="multi_level"
                          size="small"
                        >
                          <Form.Label
                            className="text-right"
                            column
                            sm={6}
                            md={6}
                            lg={4}
                          >
                            No of Levels
                          </Form.Label>
                          <Col>
                            <Form.Control
                              readOnly
                              value={this.state.registered_lease.multi_level}
                              as="input"
                              type="number"
                              size="small"
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                      <Col>
                        <Form.Group
                          as={Row}
                          controlId="multi_level_checked"
                          size="small"
                        >
                          <Col>
                            <Form.Check
                              checked={this.state.registered_lease.multi_level_checked}
                              onChange={this.handleCheckBoxChange}
                              name="registered_multi_level_checked"
                              type="checkbox"
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                      <Col className="text-right">
                        <Form.Group
                          as={Row}
                          controlId="multi_level_checked"
                          size="small"
                        >
                          <Col>
                            <Form.Check
                              checked={this.state.tenant.multi_level_checked}
                              onChange={this.handleCheckBoxChange}
                              name="tenant_multi_level_checked"
                              type="checkbox"
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                      <Col
                        sm={6}
                        md={6}
                        lg={5}
                      >
                        <Form.Group
                          as={Row}
                          controlId="multi_level"
                          size="small"
                        >
                          <Col>
                            <Form.Control
                              readOnly
                              value={this.state.tenant.multi_level}
                              as="input"
                              type="number"
                              size="small"
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col
                        sm={6}
                        md={6}
                        lg={5}
                      >
                        <Form.Group
                          as={Row}
                          controlId="level_number"
                          size="small"
                        >
                          <Form.Label
                            className="text-right"
                            column
                            sm={6}
                            md={6}
                            lg={4}
                          >
                            Level Number
                          </Form.Label>
                          <Col>
                            <Form.Control
                              readOnly
                              value={this.state.registered_lease.level_number}
                              as="input"
                              type="number"
                              size="small"
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                      <Col>
                        <Form.Group
                          as={Row}
                          controlId="level_number_checked"
                          size="small"
                        >
                          <Col>
                            <Form.Check
                              checked={this.state.registered_lease.level_number_checked}
                              onChange={this.handleCheckBoxChange}
                              name="registered_level_number_checked"
                              type="checkbox"
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                      <Col className="text-right">
                        <Form.Group
                          as={Row}
                          controlId="level_number_checked"
                          size="small"
                        >
                          <Col>
                            <Form.Check
                              checked={this.state.tenant.level_number_checked}
                              onChange={this.handleCheckBoxChange}
                              name="tenant_level_number_checked"
                              type="checkbox"
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                      <Col
                        sm={6}
                        md={6}
                        lg={5}
                      >
                        <Form.Group
                          as={Row}
                          controlId="level_number"
                          size="small"
                        >
                          <Col>
                            <Form.Control
                              readOnly
                              value={this.state.tenant.level_number}
                              as="input"
                              type="number"
                              size="small"
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col
                        sm={6}
                        md={6}
                        lg={5}
                      >
                        <Form.Group
                          as={Row}
                          controlId="floor_type"
                          size="small"
                        >
                          <Form.Label
                            className="text-right"
                            column
                            sm={6}
                            md={6}
                            lg={4}
                          >
                            Floor Type
                          </Form.Label>
                          <Col>
                            <Form.Control
                              readOnly
                              value={this.state.registered_lease.floor_type}
                              className="no-border"
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                      <Col>
                        <Form.Group
                          as={Row}
                          controlId="floor_type_checked"
                          size="small"
                        >
                          <Col>
                            <Form.Check
                              checked={this.state.registered_lease.floor_type_checked}
                              onChange={this.handleCheckBoxChange}
                              name="registered_floor_type_checked"
                              type="checkbox"
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                      <Col className="text-right">
                        <Form.Group
                          as={Row}
                          controlId="floor_type_checked"
                          size="small"
                        >
                          <Col>
                            <Form.Check
                              checked={this.state.tenant.floor_type_checked}
                              onChange={this.handleCheckBoxChange}
                              name="tenant_floor_type_checked"
                              type="checkbox"
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                      <Col
                        sm={6}
                        md={6}
                        lg={5}
                      >
                        <Form.Group
                          as={Row}
                          controlId="floor_type"
                          size="small"
                        >
                          <Col>
                            <Form.Control
                              readOnly
                              value={this.state.tenant.floor_type}
                              className="no-border"
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col
                        sm={6}
                        md={6}
                        lg={5}
                      >
                        <Form.Group
                          as={Row}
                          controlId="area"
                          size="small"
                        >
                          <Form.Label
                            className="text-right"
                            column
                            sm={6}
                            md={6}
                            lg={4}
                          >
                            Area
                          </Form.Label>
                          <Col>
                            <InputGroup>
                              <Form.Control
                                readOnly
                                value={this.state.registered_lease.area}
                                as="input"
                                type="number"
                              />
                              <InputGroup.Text>m2</InputGroup.Text>
                            </InputGroup>
                          </Col>
                        </Form.Group>
                      </Col>
                      <Col>
                        <Form.Group
                          as={Row}
                          controlId="area_checked"
                          size="small"
                        >
                          <Col>
                            <Form.Check
                              checked={this.state.registered_lease.area_checked}
                              onChange={this.handleCheckBoxChange}
                              name="registered_area_checked"
                              type="checkbox"
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                      <Col className="text-right">
                        <Form.Group
                          as={Row}
                          controlId="area_checked"
                          size="small"
                        >
                          <Col>
                            <Form.Check
                              checked={this.state.tenant.area_checked}
                              onChange={this.handleCheckBoxChange}
                              name="tenant_area_checked"
                              type="checkbox"
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                      <Col
                        sm={6}
                        md={6}
                        lg={5}
                      >
                        <Form.Group
                          as={Row}
                          controlId="area"
                          size="small"
                        >
                          <Col>
                            <InputGroup>
                              <Form.Control
                                readOnly
                                value={this.state.tenant.area}
                                as="input"
                                type="number"
                              />
                              <InputGroup.Text>m2</InputGroup.Text>
                            </InputGroup>
                          </Col>
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col
                        sm={6}
                        md={6}
                        lg={5}
                      >
                        <Form.Group
                          as={Row}
                          controlId="area_from_lease"
                          size="small"
                        >
                          <Form.Label
                            className="text-right"
                            column
                            sm={6}
                            md={6}
                            lg={4}
                          >
                            Area from Lease
                          </Form.Label>
                          <Col>
                            <Form.Check
                              readOnly
                              checked={this.state.registered_lease.area_from_lease}
                              name="area_from_lease"
                              type="checkbox"
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                      <Col>
                        <Form.Group
                          as={Row}
                          controlId="area_from_lease_checked"
                          size="small"
                        >
                          <Col>
                            <Form.Check
                              checked={this.state.registered_lease.area_from_lease_checked}
                              onChange={this.handleCheckBoxChange}
                              name="registered_area_from_lease_checked"
                              type="checkbox"
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                      <Col className="text-right">
                        <Form.Group
                          as={Row}
                          controlId="area_from_lease_checked"
                          size="small"
                        >
                          <Col>
                            <Form.Check
                              checked={this.state.tenant.area_from_lease_checked}
                              onChange={this.handleCheckBoxChange}
                              name="tenant_area_from_lease_checked"
                              type="checkbox"
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                      <Col
                        sm={6}
                        md={6}
                        lg={5}
                      >
                        <Form.Group
                          as={Row}
                          controlId="area_from_lease"
                          size="small"
                        >
                          <Col>
                            <Form.Check
                              readOnly
                              checked={this.state.tenant.area_from_lease}
                              name="area_from_lease"
                              type="checkbox"
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col
                        sm={6}
                        md={6}
                        lg={5}
                      >
                        <Form.Group
                          as={Row}
                          controlId="car_parking"
                          size="small"
                        >
                          <Form.Label
                            className="text-right"
                            column
                            sm={6}
                            md={6}
                            lg={4}
                          >
                            Car Parking
                          </Form.Label>
                          <Col>
                            <Form.Check
                              readOnly
                              checked={this.state.registered_lease.car_parking}
                              name="car_parking"
                              type="checkbox"
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                      <Col>
                        <Form.Group
                          as={Row}
                          controlId="car_parking_checked"
                          size="small"
                        >
                          <Col>
                            <Form.Check
                              checked={this.state.registered_lease.car_parking_checked}
                              onChange={this.handleCheckBoxChange}
                              name="registered_car_parking_checked"
                              type="checkbox"
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                      <Col className="text-right">
                        <Form.Group
                          as={Row}
                          controlId="car_parking_checked"
                          size="small"
                        >
                          <Col>
                            <Form.Check
                              checked={this.state.tenant.car_parking_checked}
                              onChange={this.handleCheckBoxChange}
                              name="tenant_car_parking_checked"
                              type="checkbox"
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                      <Col
                        sm={6}
                        md={6}
                        lg={5}
                      >
                        <Form.Group
                          as={Row}
                          controlId="car_parking"
                          size="small"
                        >
                          <Col>
                            <Form.Check
                              readOnly
                              checked={this.state.tenant.car_parking}
                              name="car_parking"
                              type="checkbox"
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col
                        sm={6}
                        md={6}
                        lg={5}
                      >
                        <Form.Group
                          as={Row}
                          controlId="number_of_car_parks"
                          size="small"
                        >
                          <Form.Label
                            className="text-right"
                            column
                            sm={6}
                            md={6}
                            lg={4}
                          >
                            No. Car Parks
                          </Form.Label>
                          <Col>
                            <Form.Control
                              readOnly
                              value={this.state.registered_lease.number_of_car_parks}
                              as="input"
                              type="number"
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                      <Col>
                        <Form.Group
                          as={Row}
                          controlId="number_of_car_parks_checked"
                          size="small"
                        >
                          <Col>
                            <Form.Check
                              checked={this.state.registered_lease.number_of_car_parks_checked}
                              onChange={this.handleCheckBoxChange}
                              name="registered_number_of_car_parks_checked"
                              type="checkbox"
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                      <Col className="text-right">
                        <Form.Group
                          as={Row}
                          controlId="number_of_car_parks_checked"
                          size="small"
                        >
                          <Col>
                            <Form.Check
                              checked={this.state.tenant.number_of_car_parks_checked}
                              onChange={this.handleCheckBoxChange}
                              name="tenant_number_of_car_parks_checked"
                              type="checkbox"
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                      <Col
                        sm={6}
                        md={6}
                        lg={5}
                      >
                        <Form.Group
                          as={Row}
                          controlId="number_of_car_parks"
                          size="small"
                        >
                          <Col>
                            <Form.Control
                              readOnly
                              value={this.state.tenant.number_of_car_parks}
                              as="input"
                              type="number"
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col
                        sm={6}
                        md={6}
                        lg={5}
                      >
                        <Form.Group
                          as={Row}
                          controlId="telephone"
                          size="small"
                        >
                          <Form.Label
                            className="text-right"
                            column
                            sm={6}
                            md={6}
                            lg={4}
                          >
                            Telephone
                          </Form.Label>
                          <Col>
                            <Form.Control
                              readOnly
                              value={this.state.registered_lease.telephone}
                              as="input"
                              type="text"
                              size="small"
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                      <Col>
                        <Form.Group
                          as={Row}
                          controlId="telephone_checked"
                          size="small"
                        >
                          <Col>
                            <Form.Check
                              checked={this.state.registered_lease.telephone_checked}
                              onChange={this.handleCheckBoxChange}
                              name="registered_telephone_checked"
                              type="checkbox"
                              disabled={this.state.tenant.led_head_office_lease_id ? true : false}
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                      <Col className="text-right">
                        <Form.Group
                          as={Row}
                          controlId="telephone_checked"
                          size="small"
                        >
                          <Col>
                            <Form.Check
                              checked={this.state.tenant.telephone_checked}
                              onChange={this.handleCheckBoxChange}
                              name="tenant_telephone_checked"
                              type="checkbox"
                              disabled={this.state.tenant.led_head_office_lease_id ? true : false}
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                      <Col
                        sm={6}
                        md={6}
                        lg={5}
                      >
                        <Form.Group
                          as={Row}
                          controlId="telephone"
                          size="small"
                        >
                          <Col>
                            <Form.Control
                              readOnly
                              value={this.state.tenant.telephone}
                              as="input"
                              type="text"
                              size="small"
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col
                        sm={6}
                        md={6}
                        lg={5}
                      >
                        <Form.Group
                          as={Row}
                          controlId="fax"
                          size="small"
                        >
                          <Form.Label
                            className="text-right"
                            column
                            sm={6}
                            md={6}
                            lg={4}
                          >
                            Fax
                          </Form.Label>
                          <Col>
                            <Form.Control
                              readOnly
                              value={this.state.registered_lease.fax}
                              as="input"
                              type="text"
                              size="small"
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                      <Col>
                        <Form.Group
                          as={Row}
                          controlId="fax_checked"
                          size="small"
                        >
                          <Col>
                            <Form.Check
                              checked={this.state.registered_lease.fax_checked}
                              onChange={this.handleCheckBoxChange}
                              name="registered_fax_checked"
                              type="checkbox"
                              disabled={this.state.tenant.led_head_office_lease_id ? true : false}
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                      <Col className="text-right">
                        <Form.Group
                          as={Row}
                          controlId="fax_checked"
                          size="small"
                        >
                          <Col>
                            <Form.Check
                              checked={this.state.tenant.fax_checked}
                              onChange={this.handleCheckBoxChange}
                              name="tenant_fax_checked"
                              type="checkbox"
                              disabled={this.state.tenant.led_head_office_lease_id ? true : false}
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                      <Col
                        sm={6}
                        md={6}
                        lg={5}
                      >
                        <Form.Group
                          as={Row}
                          controlId="fax"
                          size="small"
                        >
                          <Col>
                            <Form.Control
                              readOnly
                              value={this.state.tenant.fax}
                              as="input"
                              type="text"
                              size="small"
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col
                        sm={6}
                        md={6}
                        lg={5}
                      >
                        <Form.Group
                          as={Row}
                          controlId="weburl"
                          size="small"
                        >
                          <Form.Label
                            className="text-right"
                            column
                            sm={6}
                            md={6}
                            lg={4}
                          >
                            URL
                          </Form.Label>
                          <Col>
                            <InputGroup>
                              <InputGroup.Text>www.</InputGroup.Text>
                              <Form.Control
                                readOnly
                                value={this.state.registered_lease.weburl}
                                as="input"
                                type="text"
                                size="small"
                              />
                            </InputGroup>
                          </Col>
                        </Form.Group>
                      </Col>
                      <Col>
                        <Form.Group
                          as={Row}
                          controlId="weburl_checked"
                          size="small"
                        >
                          <Col>
                            <Form.Check
                              checked={this.state.registered_lease.weburl_checked}
                              onChange={this.handleCheckBoxChange}
                              name="registered_weburl_checked"
                              type="checkbox"
                              disabled={this.state.tenant.led_head_office_lease_id ? true : false}
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                      <Col className="text-right">
                        <Form.Group
                          as={Row}
                          controlId="weburl_checked"
                          size="small"
                        >
                          <Col>
                            <Form.Check
                              checked={this.state.tenant.weburl_checked}
                              onChange={this.handleCheckBoxChange}
                              name="tenant_weburl_checked"
                              type="checkbox"
                              disabled={this.state.tenant.led_head_office_lease_id ? true : false}
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                      <Col
                        sm={6}
                        md={6}
                        lg={5}
                      >
                        <Form.Group
                          as={Row}
                          controlId="weburl"
                          size="small"
                        >
                          <Col>
                            <InputGroup>
                              <InputGroup.Text>www.</InputGroup.Text>
                              <Form.Control
                                readOnly
                                value={this.state.tenant.weburl}
                                as="input"
                                type="text"
                                size="small"
                              />
                            </InputGroup>
                          </Col>
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col
                        sm={6}
                        md={6}
                        lg={5}
                      >
                        <Form.Group
                          as={Row}
                          controlId="email"
                          size="small"
                        >
                          <Form.Label
                            className="text-right"
                            column
                            sm={6}
                            md={6}
                            lg={4}
                          >
                            Email
                          </Form.Label>
                          <Col>
                            <InputGroup>
                              <InputGroup.Text>@</InputGroup.Text>
                              <Form.Control
                                readOnly
                                value={this.state.registered_lease.email}
                                as="input"
                                type="email"
                                size="small"
                              />
                            </InputGroup>
                          </Col>
                        </Form.Group>
                      </Col>
                      <Col>
                        <Form.Group
                          as={Row}
                          controlId="email_checked"
                          size="small"
                        >
                          <Col>
                            <Form.Check
                              checked={this.state.registered_lease.email_checked}
                              onChange={this.handleCheckBoxChange}
                              name="registered_email_checked"
                              type="checkbox"
                              disabled={this.state.tenant.led_head_office_lease_id ? true : false}
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                      <Col className="text-right">
                        <Form.Group
                          as={Row}
                          controlId="email_checked"
                          size="small"
                        >
                          <Col>
                            <Form.Check
                              checked={this.state.tenant.email_checked}
                              onChange={this.handleCheckBoxChange}
                              name="tenant_email_checked"
                              type="checkbox"
                              disabled={this.state.tenant.led_head_office_lease_id ? true : false}
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                      <Col
                        sm={6}
                        md={6}
                        lg={5}
                      >
                        <Form.Group
                          as={Row}
                          controlId="email"
                          size="small"
                        >
                          <Col>
                            <InputGroup>
                              <InputGroup.Text>@</InputGroup.Text>
                              <Form.Control
                                readOnly
                                value={this.state.tenant.email}
                                as="input"
                                type="email"
                                size="small"
                              />
                            </InputGroup>
                          </Col>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col
                        sm={6}
                        md={6}
                        lg={5}
                      >
                        <Form.Group
                          as={Row}
                          controlId="address_line_1"
                          size="small"
                        >
                          <Form.Label
                            className="text-right"
                            column
                            sm={6}
                            md={6}
                            lg={4}
                          >
                            Address Line 1
                          </Form.Label>
                          <Col>
                            <Form.Control
                              readOnly
                              value={this.state.registered_lease.address_line_1}
                              as="input"
                              type="text"
                              size="small"
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                      <Col>
                        <Form.Group
                          as={Row}
                          controlId="address_line_1_checked"
                          size="small"
                        >
                          <Col>
                            <Form.Check
                              checked={this.state.registered_lease.address_line_1_checked}
                              onChange={this.handleCheckBoxChange}
                              name="registered_address_line_1_checked"
                              type="checkbox"
                              disabled={this.state.tenant.led_head_office_lease_id ? true : false}
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                      <Col className="text-right">
                        <Form.Group
                          as={Row}
                          controlId="address_line_1_checked"
                          size="small"
                        >
                          <Col>
                            <Form.Check
                              checked={this.state.tenant.address_line_1_checked}
                              onChange={this.handleCheckBoxChange}
                              name="tenant_address_line_1_checked"
                              type="checkbox"
                              disabled={this.state.tenant.led_head_office_lease_id ? true : false}
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                      <Col
                        sm={6}
                        md={6}
                        lg={5}
                      >
                        <Form.Group
                          as={Row}
                          controlId="address_line_1"
                          size="small"
                        >
                          <Col>
                            <Form.Control
                              readOnly
                              value={this.state.tenant.address_line_1}
                              as="input"
                              type="text"
                              size="small"
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col
                        sm={6}
                        md={6}
                        lg={5}
                      >
                        <Form.Group
                          as={Row}
                          controlId="address_line_2"
                          size="small"
                        >
                          <Form.Label
                            className="text-right"
                            column
                            sm={6}
                            md={6}
                            lg={4}
                          >
                            Address Line 2
                          </Form.Label>
                          <Col>
                            <Form.Control
                              readOnly
                              value={this.state.registered_lease.address_line_2}
                              as="input"
                              type="text"
                              size="small"
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                      <Col>
                        <Form.Group
                          as={Row}
                          controlId="address_line_2_checked"
                          size="small"
                        >
                          <Col>
                            <Form.Check
                              checked={this.state.registered_lease.address_line_2_checked}
                              onChange={this.handleCheckBoxChange}
                              name="registered_address_line_2_checked"
                              type="checkbox"
                              disabled={this.state.tenant.led_head_office_lease_id ? true : false}
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                      <Col className="text-right">
                        <Form.Group
                          as={Row}
                          controlId="address_line_2_checked"
                          size="small"
                        >
                          <Col>
                            <Form.Check
                              checked={this.state.tenant.address_line_2_checked}
                              onChange={this.handleCheckBoxChange}
                              name="tenant_address_line_2_checked"
                              type="checkbox"
                              disabled={this.state.tenant.led_head_office_lease_id ? true : false}
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                      <Col
                        sm={6}
                        md={6}
                        lg={5}
                      >
                        <Form.Group
                          as={Row}
                          controlId="address_line_2"
                          size="small"
                        >
                          <Col>
                            <Form.Control
                              readOnly
                              value={this.state.tenant.address_line_2}
                              as="input"
                              type="text"
                              size="small"
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col
                        sm={6}
                        md={6}
                        lg={5}
                      >
                        <Form.Group
                          as={Row}
                          controlId="suburb"
                          size="small"
                        >
                          <Form.Label
                            className="text-right"
                            column
                            sm={6}
                            md={6}
                            lg={4}
                          >
                            Suburb
                          </Form.Label>
                          <Col>
                            <Form.Control
                              readOnly
                              value={this.state.registered_lease.suburb}
                              as="input"
                              type="text"
                              size="small"
                            />
                          </Col>
                        </Form.Group>
                      </Col>

                      <Col>
                        <Form.Group
                          as={Row}
                          controlId="suburb_checked"
                          size="small"
                        >
                          <Col>
                            <Form.Check
                              checked={this.state.registered_lease.suburb_checked}
                              onChange={this.handleCheckBoxChange}
                              name="registered_suburb_checked"
                              type="checkbox"
                              disabled={this.state.tenant.led_head_office_lease_id ? true : false}
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                      <Col className="text-right">
                        <Form.Group
                          as={Row}
                          controlId="suburb_checked"
                          size="small"
                        >
                          <Col>
                            <Form.Check
                              checked={this.state.tenant.suburb_checked}
                              onChange={this.handleCheckBoxChange}
                              name="tenant_suburb_checked"
                              type="checkbox"
                              disabled={this.state.tenant.led_head_office_lease_id ? true : false}
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                      <Col
                        sm={6}
                        md={6}
                        lg={5}
                      >
                        <Form.Group
                          as={Row}
                          controlId="suburb"
                          size="small"
                        >
                          <Col>
                            <Form.Control
                              readOnly
                              value={this.state.tenant.suburb}
                              as="input"
                              type="text"
                              size="small"
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col
                        sm={6}
                        md={6}
                        lg={5}
                      >
                        <Form.Group
                          as={Row}
                          controlId="state"
                          size="small"
                        >
                          <Form.Label
                            className="text-right"
                            column
                            sm={6}
                            md={6}
                            lg={4}
                          >
                            State
                          </Form.Label>
                          <Col>
                            <Form.Control
                              readOnly
                              value={this.state.registered_lease.state}
                              as="input"
                              type="text"
                              size="small"
                            />
                          </Col>
                        </Form.Group>
                      </Col>

                      <Col>
                        <Form.Group
                          as={Row}
                          controlId="state_checked"
                          size="small"
                        >
                          <Col>
                            <Form.Check
                              checked={this.state.registered_lease.state_checked}
                              onChange={this.handleCheckBoxChange}
                              name="registered_state_checked"
                              type="checkbox"
                              disabled={this.state.tenant.led_head_office_lease_id ? true : false}
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                      <Col className="text-right">
                        <Form.Group
                          as={Row}
                          controlId="state_checked"
                          size="small"
                        >
                          <Col>
                            <Form.Check
                              checked={this.state.tenant.state_checked}
                              onChange={this.handleCheckBoxChange}
                              name="tenant_state_checked"
                              type="checkbox"
                              disabled={this.state.tenant.led_head_office_lease_id ? true : false}
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                      <Col
                        sm={6}
                        md={6}
                        lg={5}
                      >
                        <Form.Group
                          as={Row}
                          controlId="state"
                          size="small"
                        >
                          <Col>
                            <Form.Control
                              readOnly
                              value={this.state.tenant.state}
                              as="input"
                              type="text"
                              size="small"
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col
                        sm={6}
                        md={6}
                        lg={5}
                      >
                        <Form.Group
                          as={Row}
                          controlId="postcode"
                          size="small"
                        >
                          <Form.Label
                            className="text-right"
                            column
                            sm={6}
                            md={6}
                            lg={4}
                          >
                            Postcode
                          </Form.Label>
                          <Col>
                            <Form.Control
                              readOnly
                              value={this.state.registered_lease.postcode}
                              as="input"
                              type="text"
                              size="small"
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                      <Col>
                        <Form.Group
                          as={Row}
                          controlId="postcode_checked"
                          size="small"
                        >
                          <Col>
                            <Form.Check
                              checked={this.state.registered_lease.postcode_checked}
                              onChange={this.handleCheckBoxChange}
                              name="registered_postcode_checked"
                              type="checkbox"
                              disabled={this.state.tenant.led_head_office_lease_id ? true : false}
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                      <Col className="text-right">
                        <Form.Group
                          as={Row}
                          controlId="postcode_checked"
                          size="small"
                        >
                          <Col>
                            <Form.Check
                              checked={this.state.tenant.postcode_checked}
                              onChange={this.handleCheckBoxChange}
                              name="tenant_postcode_checked"
                              type="checkbox"
                              disabled={this.state.tenant.led_head_office_lease_id ? true : false}
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                      <Col
                        sm={6}
                        md={6}
                        lg={5}
                      >
                        <Form.Group
                          as={Row}
                          controlId="postcode"
                          size="small"
                        >
                          <Col>
                            <Form.Control
                              readOnly
                              value={this.state.tenant.postcode}
                              as="input"
                              type="text"
                              size="small"
                            />
                          </Col>
                        </Form.Group>
                      </Col>
                    </Row>
                  </Col>
                </Row>
                <Row className="justify-content-md-center">
                  <Col
                    sm={1}
                    md={1}
                    lg={1}
                  >
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={this.handleExecuteMerge}
                    >
                      {" "}
                      Merge{" "}
                    </Button>
                  </Col>
                </Row>
              </Form>
            </Modal.Body>
          </Modal>
        )}

        {this.props.controlType === "table" && (
          <div>
            <Row style={{ marginTop: "0.75rem", marginBottom: "0.75rem" }}>
              <Col sm={12}>
                <div className="d-flex align-items-center w-100" style={{ gap: "1rem", flexWrap: "nowrap" }}>
                  <div style={{ whiteSpace: "nowrap" }}>
                    <b>Filters:</b>
                  </div>

                  <div style={{ whiteSpace: "nowrap" }}>
                    <Form.Check
                      checked={this.state.stillAnOccupantFilter}
                      onChange={this.handleStillAnOccupantFilterChange}
                      label={(
                        <>
                          Include Tenants No
                          <br />
                          Longer Here?
                        </>
                      )}
                      type="checkbox"
                      name="stillAnOccupantFilter"
                    />
                  </div>

                  <div style={{ whiteSpace: "nowrap" }}>
                    <Form.Check
                      checked={this.state.archiveFilter}
                      onChange={this.handleArchiveFilterChange}
                      label={(
                        <>
                          Include Archived
                          <br />
                          Tenants?
                        </>
                      )}
                      type="checkbox"
                      name="archivedFilter"
                    />
                  </div>

                  <div style={{ whiteSpace: "nowrap" }}>
                    <b>Sort</b> results by:
                  </div>

                  <div style={{ width: "150px", minWidth: "150px" }}>
                    <Select
                      value={this.state.selectedSortOption}
                      onChange={this.handleSortOptionChange}
                      options={this.state.sortOptions}
                      placeholder="Sort by"
                    />
                  </div>

                  <div style={{ whiteSpace: "nowrap" }}>Update</div>

                  <div style={{ width: "220px", minWidth: "220px" }}>
                    <Select
                      value={this.state.selectedTenantBuildingName && this.state.selectedTenantBuildingName.value ? this.state.selectedTenantBuildingName : null}
                      onChange={this.handleTenantBuildingNameChange}
                      options={this.state.tenantBuildingNameOptions}
                      placeholder="Tenant Building Name"
                      isClearable
                    />
                  </div>

                  <div style={{ whiteSpace: "nowrap" }}>To</div>

                  <div style={{ width: "220px", minWidth: "220px" }}>
                    <Select
                      value={this.state.selectedPropertyBuildingName && this.state.selectedPropertyBuildingName.value ? this.state.selectedPropertyBuildingName : null}
                      onChange={this.handlePropertyBuildingNameChange}
                      options={this.state.buildingNameOptions}
                      placeholder="Property Building Name"
                      isClearable
                    />
                  </div>

                  {(this.state.selectedTenantBuildingName.value || this.state.selectedPropertyBuildingName.value) && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={this.handleUpdateAllBuildings}
                    >
                      Update All Matching
                    </Button>
                  )}

                  {this.state.selectedPropertyBuildingName.value && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={this.handleUpdateSelectedBuildings}
                    >
                      Update Only Selected
                    </Button>
                  )}

                  {this.state.data.filter((f) => f.selected).length === 2 && (
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={this.handleMerge}
                    >
                      {" "}
                      Merge{" "}
                    </Button>
                  )}

                  <div style={{ marginLeft: "auto", whiteSpace: "nowrap" }}>
                    {/*   <Button variant="secondary" size="sm" onClick={this.props.refreshDataSet}  >Refresh</Button>  */}
                    <LinkContainer
                      to={{
                        pathname: `/properties/${this.props.propertykey}/leases/new`,
                        search: `?propertyid=${this.props.propertyid}`,
                      }}
                    >
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                      >
                        Add
                      </Button>
                    </LinkContainer>
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        )}
        {this.props.controlType === "table2" && (
          <Row>
            <Col>
              <b>
                {this.props.data.length} of {this.props.data.length > 0 ? this.props.data[0].result_count : "0"} records returned.
              </b>
            </Col>
          </Row>
        )}
        <Row className="show-Container">
          <Col
            sm={12}
            md={12}
            lg={12}
          >
            {!this.state.isLoading && this.props.data && this.props.leaseClassificationOptions && this.props.buildingNameOptions && this.props.floorTypeOptions ? this.renderTable(this.state.data) : this.renderTable2(this.props.data)}
          </Col>
        </Row>
      </div>
    );
  }
  render() {
    return (
      <div className="LeaseList">
        {this.state.isLoading && (
          <div className="outer-div">
            <div className="sweet-loading inner-div">
              <PacmanLoader
                cssOverride={{
                  display: "block",
                  margin: "0 auto",
                  borderColor: "red",
                }}
                size={20}
                color={"#e03c31"}
                loading={this.state.isLoading}
              />
            </div>
          </div>
        )}
        {!this.state.isLoading ? (this.props.isAuthenticated ? this.renderComponent() : null) : null}
      </div>
    );
  }
}
